import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/carl-cox-unvrs-ibiza-12-july-2026";
const IMAGE_URL = "https://raw.githubusercontent.com/HarryAdamsHLT/HeyLetsTravelFrontend/main/hey-lets-travel-frontend/public/assets/community/photo_2026-07-12_12-41-12.jpg";
const TICKET_URL = "https://www.unvrs.com/event/carl-cox/12-07-2026";
const CONCIERGE_URL = "/concierge";

const MAIN_ROOM_LINEUP = ["Carl Cox", "Chris Stussy", "Karretero"];
const WILD_COMET_LINEUP = ["Melon Bomb"];
const ALL_ARTISTS = [...MAIN_ROOM_LINEUP, ...WILD_COMET_LINEUP];

const KEYWORDS = [
  "Carl Cox UNVRS Ibiza 12 July 2026 tickets",
  "Carl Cox Ibiza tonight",
  "Chris Stussy UNVRS Ibiza",
  "Karretero UNVRS Ibiza",
  "Melon Bomb Wild Comet UNVRS",
  "UNVRS Ibiza Sunday tickets",
  "Hey Lets Travel Ibiza concierge",
];

const FAQS = [
  {
    question: "When is Carl Cox at [UNVRS] Ibiza?",
    answer: "Carl Cox is listed at [UNVRS] Ibiza on Sunday 12 July 2026, with the event artwork showing a 23:30 start time.",
  },
  {
    question: "Who is playing with Carl Cox at [UNVRS] on 12 July 2026?",
    answer: "The listed lineup is Carl Cox, Chris Stussy and Karretero, with Melon Bomb listed for Wild Comet.",
  },
  {
    question: "Where can I buy Carl Cox [UNVRS] tickets?",
    answer: "Use the official [UNVRS] event link in this HLT guide. The artwork shows tickets from 95 EUR, but live pricing and availability can change.",
  },
  {
    question: "Where is [UNVRS] Ibiza?",
    answer: "[UNVRS] is in the San Rafael area of Ibiza, on the former Privilege Ibiza site, and is run by The Night League team behind Hï Ibiza and Ushuaïa Ibiza.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Carl Cox at [UNVRS] Ibiza 12 July 2026: Tickets, Lineup and HLT Guide",
  description:
    "Carl Cox plays [UNVRS] Ibiza on Sunday 12 July 2026 with Chris Stussy, Karretero and Melon Bomb at Wild Comet. Official ticket link and HLT planning guide.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-07-12",
  dateModified: "2026-07-12",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Carl Cox at [UNVRS] Ibiza",
  startDate: "2026-07-12T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "[UNVRS] Ibiza",
    address: "San Rafael, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "95",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Carl Cox Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function CarlCoxUnvrsArticle() {
  return (
    <>
      <Helmet>
        <title>Carl Cox [UNVRS] Ibiza 12 July 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="Carl Cox at [UNVRS] Ibiza on Sunday 12 July 2026 with Chris Stussy, Karretero and Melon Bomb at Wild Comet. Official ticket link and HLT guide."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Carl Cox [UNVRS] Ibiza 12 July 2026 Tickets" />
        <meta property="og:description" content="Official ticket link, lineup and HLT planning guide for Carl Cox at [UNVRS] Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Carl Cox [UNVRS] Ibiza 12 July 2026 Tickets" />
        <meta name="twitter:description" content="Carl Cox, Chris Stussy, Karretero and Melon Bomb at [UNVRS] Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Carl Cox at [UNVRS] Ibiza: 12 July 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Carl Cox lands at [UNVRS] tonight for a Sunday session with real Ibiza weight: Chris Stussy and Karretero on the main bill, plus Melon Bomb taking the Wild Comet thread.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official [UNVRS] tickets" />
              <ConciergeCta children="Plan [UNVRS] with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Carl Cox at [UNVRS]</dd></div>
              <div><dt className="text-white">Venue</dt><dd>[UNVRS] Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Sunday 12 July 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>From 95 EUR on the event artwork</dd></div>
              <div><dt className="text-white">Sound</dt><dd>House, techno, deep groove and Balearic club pressure</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_URL} alt="Carl Cox at [UNVRS] Ibiza 12 July 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                Carl Cox and Ibiza are not a casual pairing. His long Space Ibiza chapter helped define the island's modern house and techno identity, and seeing him now at [UNVRS] gives the night a clear sense of occasion. This is one of those bookings where the room, the history and the sound all line up.
              </p>
              <p>
                [UNVRS] brings the scale. The former Privilege site has been reworked into a new-generation hyperclub, and Sunday is built around Carl Cox's ability to hold a huge room without flattening the music into simple festival hits.
              </p>
            </ArticleSection>

            <ArticleSection title="Main room: Carl Cox, Chris Stussy and Karretero">
              <p>
                Carl Cox is the anchor: big-room control, heavyweight drums and a deep understanding of how an Ibiza crowd moves after midnight. Expect a set that can move through techno, house and rolling club pressure without losing its personality.
              </p>
              <p>
                Chris Stussy is the sharp modern counterpoint. His sound sits in that clean, deep, minimal house lane that has become one of the most in-demand corners of the current scene. Karretero adds another underground edge to the night, giving the main room a wider, club-focused shape before Carl Cox takes it higher.
              </p>
            </ArticleSection>

            <ArticleSection title="Wild Comet: Melon Bomb">
              <p>
                Melon Bomb on Wild Comet gives the event a more Balearic side. The Ibiza collective is known for warm, feel-good house selections, so this is the room to use when you want a looser island energy before stepping back into the Carl Cox main-room pressure.
              </p>
              <p>
                The smart move is to treat the night as a full [UNVRS] experience rather than one set. Start with the groove, move through Wild Comet, then lock into the main room when the room starts to peak.
              </p>
            </ArticleSection>

            <ArticleSection title="Ticket link and timing">
              <p>
                We are linking directly to the official [UNVRS] Carl Cox event page for Sunday 12 July 2026. The supplied event artwork shows entry from 95 EUR, but live availability and final pricing can change quickly on the day.
              </p>
              <p>
                The event is listed for 23:30. [UNVRS] is in the San Rafael area, so plan transport before you leave, especially if your group is staying in Playa d'en Bossa, Ibiza Town or San Antonio.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on [UNVRS]" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Where to stay for [UNVRS]">
              <p>
                Ibiza Town is a strong base if you want dinner, marina energy and a clean transfer route before heading to San Rafael. Playa d'en Bossa works for groups mixing [UNVRS] with Hï Ibiza or Ushuaïa across the same trip, while San Antonio can make sense if sunset plans and budget are part of the weekend.
              </p>
              <p>
                HLT can help shape the night around the ticket: hotels, villa options, private transport, tables, after-party planning and the parts of the trip that tend to get messy when a group leaves everything until the last minute.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><span className="text-white">Event:</span> Carl Cox at [UNVRS] Ibiza</li>
                <li><span className="text-white">Date:</span> Sunday 12 July 2026</li>
                <li><span className="text-white">Start time:</span> 23:30</li>
                <li><span className="text-white">Lineup:</span> Carl Cox, Chris Stussy, Karretero</li>
                <li><span className="text-white">Wild Comet:</span> Melon Bomb</li>
                <li><span className="text-white">Tickets:</span> From 95 EUR on the event artwork</li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                If tonight is your one proper club night in Ibiza, Carl Cox at [UNVRS] is a serious pick. It has the heritage, the production scale and enough lineup depth to feel like more than a standard headline show.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Get official Carl Cox tickets" />
                <ConciergeCta children="Build the Ibiza trip" />
              </div>
            </ArticleSection>

            <ArticleSection title="FAQ">
              <div className="space-y-5">
                {FAQS.map((item) => (
                  <div key={item.question}>
                    <h3 className="font-semibold text-white">{item.question}</h3>
                    <p>{item.answer}</p>
                  </div>
                ))}
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/25 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Book direct</p>
              <h2 className="mt-3 font-display text-3xl leading-none text-white">Official ticket link</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Use the official [UNVRS] listing for live availability and final pricing.
              </p>
              <div className="mt-4">
                <TicketCta children="Open tickets" />
              </div>
            </div>

            <div className="border border-white/10 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT concierge</p>
              <h2 className="mt-3 font-display text-3xl leading-none text-white">Need the full night handled?</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Tell us your group size, hotel area and budget. We can help with transfers, tables, villas and a cleaner plan for the night.
              </p>
              <div className="mt-4">
                <ConciergeCta children="Start concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-[#0d0d0d] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-300">
                <li>Bring photo ID and use the name shown on your ticket.</li>
                <li>Pre-book transport for San Rafael late-night returns.</li>
                <li>Check the official ticket page before travelling, as pricing can move.</li>
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}
